import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import { queryKeys } from "../../lib/queryKeys";
import {
  Badge,
  Code,
  DataTable,
  Drawer,
  EmptyState,
  InlineError,
  KeyValue,
  Section,
  Skeleton,
  Stack,
  type KeyValueRow,
} from "../../ui";

type AlertChannel = components["schemas"]["AlertChannelResponse"];
type AlertDelivery = components["schemas"]["AlertDelivery"];
type AlertEvent = components["schemas"]["AlertEventResponse"];
type AlertRule = components["schemas"]["AlertRuleResponse"];

function payloadValue(value: unknown) {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return <Code>{String(value)}</Code>;
  }
  if (value == null) {
    return "none";
  }
  return <Code>{JSON.stringify(value)}</Code>;
}

function eventRows(
  event: AlertEvent,
  rule: AlertRule | undefined,
): KeyValueRow[] {
  return [
    { label: "Rule", value: rule?.display_name ?? "Deleted alert rule" },
    { label: "Severity", value: rule?.severity ?? "unavailable" },
    { label: "State", value: event.event_type },
    { label: "Created", value: formatDateTime(event.created_at) },
    { label: "Event key", value: <Code>{event.event_key}</Code> },
  ];
}

function deliveryColumns(
  channels: AlertChannel[],
): Array<ColumnDef<AlertDelivery>> {
  return [
    {
      header: "Channel",
      id: "channel",
      accessorFn: (delivery) =>
        channels.find((channel) => channel.id === delivery.channel_id)
          ?.display_name ?? "Deleted channel",
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <Badge tone={row.original.status === "failed" ? "danger" : "neutral"}>
          {row.original.status}
        </Badge>
      ),
    },
    { header: "Attempts", accessorKey: "attempts" },
    {
      header: "Last error",
      accessorKey: "last_error",
      cell: ({ row }) => row.original.last_error ?? "none",
    },
    {
      header: "Updated",
      accessorKey: "at",
      cell: ({ row }) => formatDateTime(row.original.at),
    },
  ];
}

export interface AlertEventDrawerProps {
  channels: AlertChannel[];
  eventId: string;
  onDismiss: () => void;
  rules: AlertRule[];
}

export interface AlertEventDrawerViewProps {
  channels: AlertChannel[];
  errorMessage?: string;
  event?: AlertEvent;
  isPending: boolean;
  onDismiss: () => void;
  rules: AlertRule[];
}

export function AlertEventDrawerView({
  channels,
  errorMessage,
  event,
  isPending,
  onDismiss,
  rules,
}: AlertEventDrawerViewProps) {
  const rule = event
    ? rules.find((candidate) => candidate.id === event.alert_rule_id)
    : undefined;
  const deliveries = event?.deliveries ?? [];

  return (
    <Drawer
      ariaLabel="Alert event detail"
      isOpen
      onDismiss={onDismiss}
      size="lg"
    >
      {isPending ? (
        <Skeleton label="Loading alert event" rows={6} shape="detail-field" />
      ) : errorMessage ? (
        <InlineError>{errorMessage}</InlineError>
      ) : event ? (
        <Stack gap={5}>
          <KeyValue rows={eventRows(event, rule)} />
          <Section heading="Payload">
            <KeyValue
              rows={Object.entries(event.payload).map(([key, value]) => ({
                label: key.replaceAll("_", " "),
                value: payloadValue(value),
              }))}
            />
          </Section>
          <Section heading="Deliveries">
            {deliveries.length > 0 ? (
              <DataTable
                ariaLabel="Alert event deliveries"
                columns={deliveryColumns(channels)}
                data={deliveries}
                getRowId={(delivery) => delivery.channel_id}
              />
            ) : (
              <EmptyState title="No deliveries" />
            )}
          </Section>
        </Stack>
      ) : (
        <InlineError>Alert event data is unavailable.</InlineError>
      )}
    </Drawer>
  );
}

export function AlertEventDrawer({
  channels,
  eventId,
  onDismiss,
  rules,
}: AlertEventDrawerProps) {
  const event = useQuery({
    queryKey: queryKeys.alertEvents.detail(eventId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/alert-events/{id}", {
        params: { path: { id: eventId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  return (
    <AlertEventDrawerView
      channels={channels}
      errorMessage={event.isError ? event.error.message : undefined}
      event={event.data}
      isPending={event.isPending}
      onDismiss={onDismiss}
      rules={rules}
    />
  );
}
